import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useGetIdentityLocations } from '../../../../domain/Users';
import useClampedPage from '../../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../../commonComponents/DsPagination';
import { LocationsTable } from './LocationsTable';

/**
 * Locations (GOTU-19603): every location the identity can access, directly or
 * through a group or organization grant, from organization-service.
 *
 * Search and paging are server-side, since an identity can reach more than a
 * thousand locations; this tab holds the two values and hands them to the
 * query.
 */
export const LocationsTab = ({ identityId }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const {
    locations, totalCount, pageSize, isPending, isFetching, isError, refetch,
  } = useGetIdentityLocations(identityId, { page, q: search });

  // Narrowing the search can leave the page past the last one; pull it back.
  useClampedPage(page, totalCount, pageSize, setPage);

  // Page 4 of every location is not page 4 of the matches.
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <LocationsTable
      locations={locations}
      // The query keeps the last answer while the next page or search loads,
      // and those rows would sit under a pager, or an empty copy, that already
      // belongs to the next one. The spinner stands in until it lands; the
      // search box stays mounted, so typing keeps its focus.
      isPending={isPending || isFetching}
      isError={isError}
      hasSearch={search.trim() !== ''}
      onRetry={() => refetch()}
      toolbar={(
        <DebouncedSearchField
          label="Search"
          placeholder="Location name"
          value={search}
          onChange={handleSearchChange}
        />
      )}
      footer={(
        <DsPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          onPageChange={setPage}
        />
      )}
    />
  );
};

LocationsTab.propTypes = {
  identityId: PropTypes.string.isRequired,
};

export default LocationsTab;
